/**
 * The entities the content service HTML-escapes on its way out — `titleTag` and
 * `metaDescription` render as plain text, so "photo &amp; film" would reach the `<title>`.
 */
const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  // Escaped rather than written as a literal U+00A0, which is indistinguishable from a
  // plain space in source and which a formatter or editor can silently normalise to one.
  "&nbsp;": "\u00a0",
};

/** Matches only the entities above — anything else is left as authored. */
const ENTITY_PATTERN = /&(?:amp|lt|gt|quot|#39|apos|nbsp);/g;

/**
 * Decodes the entities above in one left-to-right pass, so `&amp;lt;` stops at `&lt;`.
 * **Text content only — never `innerHTML`/`dangerouslySetInnerHTML`**: `&lt;script&gt;`
 * comes back as `<script>`, safe as React children or a `<meta>` attribute, not as markup.
 */
export const decodeHtmlEntities = (text: string): string =>
  text.replace(ENTITY_PATTERN, (entity) => HTML_ENTITIES[entity] ?? entity);

/** Captures the href out of Strapi's anchor-wrapped external links. */
const ANCHOR_HREF = /href="([^"]+)"/;

/**
 * Unwraps a Strapi link: external ones arrive wrapped in anchor markup, which as
 * an `href` would break every external CTA. Passes through when already bare.
 */
export const resolveStrapiLink = (link?: string | null): string | undefined => {
  if (!link) {
    return undefined;
  }

  return ANCHOR_HREF.exec(link)?.[1] ?? link;
};
